import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Megaphone, Calendar, Pin, ChevronDown, ChevronUp, ExternalLink,
    AlertCircle, Info, CheckCircle, FileText, User
} from 'lucide-react'


interface Announcement {
    id: string
    title: string
    content: string
    type: 'info' | 'important' | 'success' | 'alert'
    date: string
    author: string
    pinned: boolean
    attachments?: { name: string; url: string }[]
}

const announcements: Announcement[] = [
    {
        id: '1',
        title: '🎉 NEET 2025 Registration Opens',
        content: 'The National Testing Agency has announced that NEET 2025 registrations will begin from November 1, 2024. Make sure to keep your documents ready. Required documents include: Aadhaar card, Class 10 & 12 marksheets, passport-sized photographs, and category certificates if applicable.',
        type: 'important',
        date: 'Oct 20, 2024',
        author: 'Admin',
        pinned: true,
        attachments: [{ name: 'NEET 2025 Guidelines.pdf', url: '#' }]
    },
    {
        id: '2',
        title: 'Weekly Mock Test 7 Scheduled',
        content: 'Your next weekly mock test is scheduled for Saturday, October 26th at 10:00 AM. The test will cover all topics from Physics (Thermodynamics, Optics), Chemistry (Organic Chemistry), and Biology (Human Physiology). Duration: 3 hours.',
        type: 'info',
        date: 'Oct 18, 2024',
        author: 'Dr. Sarah Patel',
        pinned: true
    },
    {
        id: '3',
        title: 'New Study Material Added',
        content: 'We have added comprehensive notes for Genetics and Ecology in the Study Material section. Please download and review before the upcoming biology revision session.',
        type: 'success',
        date: 'Oct 17, 2024',
        author: 'Dr. Meera Singh',
        pinned: false
    },
    {
        id: '4',
        title: 'Doubt Clearing Session - Chemistry',
        content: 'Prof. Rajesh Kumar will conduct a live doubt clearing session for Organic Chemistry on October 25th from 4:00 PM to 6:00 PM. Join via the Chat section.',
        type: 'info',
        date: 'Oct 16, 2024',
        author: 'Prof. Rajesh Kumar',
        pinned: false
    },
    {
        id: '5',
        title: 'Fee Reminder',
        content: 'This is a reminder that the November batch fee is due by October 31st. Please complete the payment to avoid any interruption in your course access.',
        type: 'alert',
        date: 'Oct 15, 2024',
        author: 'Accounts Team',
        pinned: false
    },
    {
        id: '6',
        title: 'Congratulations to Top Performers!',
        content: 'Congratulations to Arun, Priya, and Rahul for securing the top 3 positions in the Weekly Mock Test 6. Keep up the excellent work!',
        type: 'success',
        date: 'Oct 14, 2024',
        author: 'Admin',
        pinned: false
    },
]

const typeConfig = {
    info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    important: { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30' },
    success: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30' },
    alert: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' },
}

export function AnnouncementsView() {
    const [expandedId, setExpandedId] = useState<string | null>(null)
    const [filter, setFilter] = useState<'all' | 'pinned'>('all')

    const pinnedAnnouncements = announcements.filter(a => a.pinned)
    const filteredAnnouncements = filter === 'pinned' ? pinnedAnnouncements : announcements


    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary flex items-center gap-2">
                    <Megaphone className="h-6 w-6 text-primary-blue" />
                    Announcements
                </h2>
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                    {(['all', 'pinned'] as const).map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-1.5 text-sm font-medium rounded-md capitalize transition-all ${filter === f
                                ? 'bg-white dark:bg-slate-700 shadow text-primary-blue'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            {f} {f === 'pinned' && `(${pinnedAnnouncements.length})`}
                        </button>
                    ))}
                </div>
            </div>

            {/* Announcements List */}
            <div className="space-y-3">
                {filteredAnnouncements.map((announcement, i) => {
                    const config = typeConfig[announcement.type]
                    const Icon = config.icon
                    const isExpanded = expandedId === announcement.id

                    return (
                        <motion.div
                            key={announcement.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                        >
                            <GlassmorphicCard className={`overflow-hidden ${announcement.pinned ? 'border-l-4 border-l-primary-blue' : ''}`}>
                                <button
                                    onClick={() => setExpandedId(isExpanded ? null : announcement.id)}
                                    className="w-full p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center shrink-0`}>
                                            <Icon className={`h-5 w-5 ${config.color}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                {announcement.pinned && (
                                                    <Pin className="h-3.5 w-3.5 text-primary-blue" />
                                                )}
                                                <h3 className="font-semibold text-theme-primary text-sm md:text-base">{announcement.title}</h3>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-theme-secondary">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {announcement.date}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <User className="h-3 w-3" />
                                                    {announcement.author}
                                                </span>
                                            </div>
                                        </div>
                                        {isExpanded ? (
                                            <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
                                        ) : (
                                            <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                                        )}
                                    </div>
                                </button>

                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="border-t border-slate-100 dark:border-slate-800"
                                        >
                                            <div className="p-4 bg-slate-50 dark:bg-slate-900/50">
                                                <p className="text-theme-secondary text-sm leading-relaxed mb-4">
                                                    {announcement.content}
                                                </p>

                                                {announcement.attachments && announcement.attachments.length > 0 && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {announcement.attachments.map((file, idx) => (
                                                            <a
                                                                key={idx}
                                                                href={file.url}
                                                                className="inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-primary-blue hover:bg-primary-blue hover:text-white transition-colors"
                                                            >
                                                                <FileText className="h-4 w-4" />
                                                                {file.name}
                                                                <ExternalLink className="h-3 w-3" />
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </GlassmorphicCard>
                        </motion.div>
                    )
                })}
            </div>

            {filteredAnnouncements.length === 0 && (
                <GlassmorphicCard className="p-12 text-center">
                    <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-theme-secondary">No announcements found</p>
                </GlassmorphicCard>
            )}
        </div>
    )
}
